import type { Task } from "@app/core";
import { Card, Check, CheckCircle2, cn } from "@app/ui";

import { AnimatePresence, motion, springs, useReducedMotion } from "../../lib/motion/index.js";
import { useAppLocale } from "../../lib/useAppLocale.js";
import { useI18n } from "../../lib/useI18n.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { agendaDayLabel, dayProgress, isPastSlot, nowLineIndex } from "./agenda.js";
import { AgendaRow } from "./AgendaRow.js";
import { dayAgendaNavKey } from "./useAgendaKeyboardNav.js";

interface Props {
  date: Date;
  /** The day's items, view-filtered but NOT done-filtered (progress needs all). */
  items: Task[];
  now: Date;
  isToday: boolean;
  hideDone: boolean;
  focusedKey?: string | null | undefined;
  onToggle: (task: Task, done: boolean) => void;
  onEdit: (task: Task) => void;
  onView: (task: Task) => void;
  onSnooze: (task: Task, pos: { x: number; y: number }) => void;
  /** Future-day rows get the sun quick-action (pull the task onto today). */
  onMoveToToday?: ((task: Task, pos: { x: number; y: number }) => void) | undefined;
  /** Time-chip reschedule within this day (movable one-offs only). */
  onReschedule?: ((task: Task, pos: { x: number; y: number }) => void) | undefined;
  /** Makes the day header clickable — jump to this day on the calendar. */
  onOpenDay?: ((date: Date) => void) | undefined;
  /** Render at most this many rows. The route pages the whole upcoming stream
   *  against one row budget, and the day it runs out on is rendered partially;
   *  the header's counts stay over the FULL day so they never lie. */
  limit?: number | undefined;
}

/**
 * One agenda day as a card: in-card header with the day label, open count and
 * an animated completion bar along its bottom edge; the row list below.
 * Today marks itself with a primary dot and celebrates when every task is done.
 */
export function DaySection({
  date, items, now, isToday, hideDone, focusedKey,
  onToggle, onEdit, onView, onSnooze, onMoveToToday, onReschedule, onOpenDay, limit,
}: Props) {
  const { t } = useI18n();
  const locale = useAppLocale();
  const reduce = useReducedMotion();
  const relativeLabels = { today: t("today.relative.today"), tomorrow: t("today.relative.tomorrow") };

  const progress = dayProgress(items);
  const open = progress.total - progress.done;
  const allDone = progress.total > 0 && open === 0;
  const visible = hideDone ? items.filter((x) => x.status !== "done") : items;
  const shown = limit === undefined || visible.length <= limit ? visible : visible.slice(0, limit);
  // "Now" line index over the SHOWN list (today only) so hiding done items
  // can't strand the divider next to rows that aren't rendered.
  const divider = isToday ? nowLineIndex(shown, now) : -1;

  return (
    <section>
      <Card className={cn("overflow-hidden p-0", isToday && "ring-1 ring-primary/25")}>
        <header className="relative flex items-center justify-between gap-2 border-b bg-muted/30 px-3 py-2">
          <h2 className="min-w-0 text-sm font-semibold">
            {onOpenDay ? (
              <button
                type="button"
                onClick={() => onOpenDay(date)}
                title={t("today.openInCalendar")}
                className={cn(
                  "-mx-1 flex min-w-0 items-center gap-2 rounded-md px-1 text-left leading-tight transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  isToday && "text-primary",
                )}
              >
                {isToday && <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-primary" />}
                <span className="min-w-0 break-words">{agendaDayLabel(date, now, locale, relativeLabels)}</span>
              </button>
            ) : (
              <span className={cn("flex items-center gap-2", isToday && "text-primary")}>
                {isToday && <span aria-hidden className="h-2 w-2 rounded-full bg-primary" />}
                {agendaDayLabel(date, now, locale, relativeLabels)}
              </span>
            )}
          </h2>
          {progress.total > 0 && (
            <span className={cn("flex shrink-0 items-center gap-1 whitespace-nowrap text-xs", allDone ? "font-medium text-emerald-600 dark:text-emerald-400" : "text-muted-foreground")}>
              {allDone && <Check className="h-3.5 w-3.5" aria-hidden />}
              {allDone ? t("today.dayDone") : t("today.openCount", { count: open })}
            </span>
          )}
          {progress.total > 0 && (
            <span aria-hidden className="absolute inset-x-0 bottom-0 h-[3px]">
              <motion.span
                className="block h-full rounded-r-full bg-gradient-to-r from-primary to-emerald-500"
                initial={reduce ? false : { width: 0 }}
                animate={{ width: `${(progress.done / progress.total) * 100}%` }}
                transition={reduce ? { duration: 0 } : springs.smooth}
              />
            </span>
          )}
        </header>

        {isToday && allDone && (
          <motion.div
            initial={reduce ? false : { opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={reduce ? { duration: 0 } : springs.smooth}
            className="flex items-center gap-2 border-b bg-emerald-500/10 px-3 py-2.5 text-sm font-medium text-emerald-600 dark:text-emerald-300"
          >
            <motion.span
              initial={reduce ? false : { scale: 0, rotate: -90 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={reduce ? { duration: 0 } : springs.bouncy}
              className="grid shrink-0 place-items-center"
            >
              <CheckCircle2 className="h-5 w-5" aria-hidden />
            </motion.span>
            {t("today.allClear")}
          </motion.div>
        )}

        {shown.length === 0 ? (
          items.length === 0 ? (
            <p className="px-3 py-3 text-sm text-muted-foreground">
              {isToday ? t("today.empty") : t("today.emptyDay")}
            </p>
          ) : (
            !(isToday && allDone) && (
              <p className="flex items-center gap-1.5 px-3 py-3 text-sm text-muted-foreground">
                <Check className="h-4 w-4 text-emerald-500" aria-hidden /> {t("today.dayDone")}
              </p>
            )
          )
        ) : (
          <ul className="flex flex-col divide-y divide-border text-sm">
            <AnimatePresence initial={false}>
              {shown.flatMap((task, i) => {
                const row = (
                  <AgendaRow
                    key={task.id}
                    task={task}
                    dimmed={isToday && isPastSlot(task, now)}
                    navKey={dayAgendaNavKey(date, task.id)}
                    focusedKey={focusedKey}
                    onToggle={onToggle}
                    onEdit={onEdit}
                    onView={onView}
                    onSnooze={onSnooze}
                    onMoveToToday={onMoveToToday}
                    onReschedule={onReschedule}
                  />
                );
                return divider === i ? [<NowLine key="now-line" now={now} />, row] : [row];
              })}
            </AnimatePresence>
          </ul>
        )}
      </Card>
    </section>
  );
}

/** The "now" line inside today's list: pulsing dot, primary rule, current
 *  time. Glides between rows (`layout`) as items pass — cheap, since the list
 *  only re-renders on the minute tick anyway. */
function NowLine({ now }: { now: Date }) {
  const { t } = useI18n();
  const tf = useTimeFormat();
  const reduce = useReducedMotion();
  return (
    <motion.li
      aria-hidden
      layout={!reduce}
      initial={reduce ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0 } }}
      transition={reduce ? { duration: 0 } : springs.smooth}
      className="flex items-center gap-2 px-3 py-1"
    >
      <span className="relative grid h-2 w-2 shrink-0 place-items-center">
        <span className="live-pulse absolute h-full w-full rounded-full bg-primary" />
        <span className="h-1.5 w-1.5 rounded-full bg-primary" />
      </span>
      <span className="h-px flex-1 bg-primary/40" />
      <span className="shrink-0 text-[calc(10px*var(--app-text-scale))] font-semibold uppercase tracking-wider tabular-nums text-primary">
        {t("today.now")} · {tf.hmDate(now)}
      </span>
    </motion.li>
  );
}
